/* ControlArticleDetailPage — §34-40 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { controlApi, useOnlineStatus } from "../lib/controlApi";
import { STATUS_STYLE } from "../ControlApp";
import { ChevronLeft, Loader2, Trash2, RefreshCw, Facebook, Instagram, Globe, AlertCircle, Pencil } from "lucide-react";

function humanReason(r) {
  const map = {
    daily_limit: "Límite diario alcanzado.",
    missing_visual: "Sin imagen apta.",
    score_below_threshold: "Prioridad insuficiente.",
    saturation: "Muchas publicaciones del mismo evento.",
    admin_opt_out: "Bloqueada por el administrador.",
    channel_paused: "Canal pausado.",
    rate_limit: "Meta limitó temporalmente.",
    article_removed: "Noticia retirada.",
    already_queued_or_published: "Ya publicada o en cola.",
    urgent_selected: "Seleccionada por urgencia.",
    auto_selected: "Selección automática.",
    admin_forced_selected: "Forzada por el administrador.",
  };
  return map[r] || r || "—";
}

function newKey() { return (crypto && crypto.randomUUID && crypto.randomUUID()) || String(Date.now()); }

export default function ControlArticleDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const online = useOnlineStatus();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reason, setReason] = useState("");
  const [showDistribution, setShowDistribution] = useState(false);
  const [publishSocialOpen, setPublishSocialOpen] = useState(false);
  const [socialSel, setSocialSel] = useState({ facebook: true, instagram: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await controlApi.article(id);
      setData(r);
    } catch (ex) {
      const detail = ex && ex.response && ex.response.data && ex.response.data.detail;
      const msg = (detail && detail.error && detail.error.message) || "No se pudo cargar la noticia.";
      toast.error(msg);
    } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-6 text-textgray text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>;
  if (!data) return <div className="p-6">
    <button onClick={() => nav(-1)} className="text-nightblue text-sm underline">Volver</button>
  </div>;

  const art = data.article || {};
  const socials = data.social || [];
  const fb = socials.find((s) => s.platform === "facebook");
  const ig = socials.find((s) => s.platform === "instagram");
  const version = data.version || art.updated_at;

  const doDelete = async () => {
    if (!online) { toast.error("Esta acción requiere conexión."); return; }
    setBusy("delete");
    try {
      const r = await controlApi.removeArticle(id, reason, version, newKey());
      toast.success(`Retirada del portal. ${r.cancelled_pending_social} publicación${r.cancelled_pending_social === 1 ? "" : "es"} social${r.cancelled_pending_social === 1 ? "" : "es"} pendiente${r.cancelled_pending_social === 1 ? "" : "s"} cancelada${r.cancelled_pending_social === 1 ? "" : "s"}.`);
      nav("/control/articles", { replace: true });
    } catch (ex) {
      const detail = ex && ex.response && ex.response.data && ex.response.data.detail;
      const code = detail && detail.error && detail.error.code;
      if (code === "VERSION_CONFLICT") {
        toast.error("Este contenido fue modificado desde otro dispositivo.");
        load();
      } else {
        toast.error((detail && detail.error && detail.error.message) || "No se pudo eliminar.");
      }
    } finally { setBusy(""); setConfirmDelete(false); }
  };

  const doRetry = async (channel) => {
    if (!online) { toast.error("Esta acción requiere conexión."); return; }
    setBusy(`retry-${channel}`);
    try {
      const fn = channel === "facebook" ? controlApi.retryFacebook : controlApi.retryInstagram;
      await fn(id, newKey());
      toast.success(`Reintento de ${channel === "facebook" ? "Facebook" : "Instagram"} en cola.`);
      load();
    } catch (ex) {
      const detail = ex && ex.response && ex.response.data && ex.response.data.detail;
      toast.error((detail && detail.error && detail.error.message) || "No se pudo reintentar.");
    } finally { setBusy(""); }
  };

  const doPublishSocial = async () => {
    const platforms = Object.keys(socialSel).filter((k) => socialSel[k]);
    if (platforms.length === 0) { toast.error("Elegí al menos una plataforma"); return; }
    if (!online) { toast.error("Esta acción requiere conexión."); return; }
    setBusy("publish-social");
    try {
      const r = await controlApi.publishSocial(id, platforms);
      if (r?.status === "queued") {
        toast.success(`Publicando en ${platforms.join(" + ")}…`);
      } else if (r?.status === "all_already_published") {
        toast.info("Todas las redes ya estaban publicadas");
      }
      setPublishSocialOpen(false);
      setTimeout(load, 1500);
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      toast.error((typeof detail === "string" ? detail : detail?.error?.message) || "No se pudo publicar en redes");
    } finally { setBusy(""); }
  };

  const patchDist = async (patch) => {
    if (!online) { toast.error("Esta acción requiere conexión."); return; }
    setBusy("dist");
    try {
      await controlApi.patchDistribution(id, { ...patch, expected_version: version }, newKey());
      toast.success("Distribución actualizada.");
      load();
    } catch (ex) {
      const detail = ex && ex.response && ex.response.data && ex.response.data.detail;
      const code = detail && detail.error && detail.error.code;
      if (code === "VERSION_CONFLICT") {
        toast.error("Cambió desde otro dispositivo.");
        load();
      } else toast.error((detail && detail.error && detail.error.message) || "Error");
    } finally { setBusy(""); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="spc-article-detail">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-textgray text-sm">
        <ChevronLeft className="w-4 h-4" /> Volver
      </button>
      <div className="border border-softblue bg-white">
        {art.image_url && (
          <img src={art.image_url} alt="" loading="lazy" className="w-full aspect-video object-cover bg-slate-100" />
        )}
        <div className="p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-textgray flex flex-wrap gap-2">
            {art.published_at && <span>{new Date(art.published_at).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" })}</span>}
            {art.location_id && <span>· {art.location_id}</span>}
            {art.main_category_name && <span>· {art.main_category_name}</span>}
            {typeof art.editorial_score === "number" && <span className="font-bold text-blueblack">Score {art.editorial_score}</span>}
            {art.urgent && <span className="text-red-600 font-bold">URGENTE</span>}
          </div>
          <h1 className="font-serif text-xl font-bold text-blueblack leading-tight">{art.title}</h1>
          {art.summary && <p className="text-sm text-textgray">{art.summary}</p>}
          <div className="text-[11px] text-textgray flex flex-wrap gap-3 pt-2">
            {data.event && <span>Evento: <b>{data.event.title || data.event.id}</b></span>}
            {art.content_origin && <span>Origen: <b>{art.content_origin}</b></span>}
            <span>Estado: <b>{art.status}</b></span>
          </div>
        </div>
      </div>

      {/* Distribución (§35) */}
      <div className="border border-softblue bg-white">
        <div className="px-4 py-2 border-b border-softblue text-[10px] uppercase tracking-widest text-textgray font-bold">Distribución</div>
        <div className="divide-y divide-softblue">
          <ChannelRow icon={Globe} label="PORTAL" status={art.status === "published" ? "published" : art.status} at={art.published_at} />
          <ChannelRow icon={Facebook} label="FACEBOOK" status={(fb && fb.status) || "not_selected"}
            at={(fb && fb.published_at) || (fb && fb.scheduled_at)} reason={fb && (fb.error_code || fb.selection_reason)}
            permalink={fb && fb.permalink} nextRetry={fb && fb.next_retry_at} />
          <ChannelRow icon={Instagram} label="INSTAGRAM" status={(ig && ig.status) || "not_selected"}
            at={(ig && ig.published_at) || (ig && ig.scheduled_at)} reason={ig && (ig.error_code || ig.selection_reason)}
            permalink={ig && ig.permalink} nextRetry={ig && ig.next_retry_at} />
        </div>
      </div>

      {/* Acciones (§36) */}
      <div className="grid grid-cols-2 gap-2">
        {art.status !== "removed" && (
          <button onClick={() => nav(`/control/editor/article/${id}`)} data-testid="spc-edit-article-btn"
            className="min-h-[44px] border border-blueblack bg-blueblack text-white text-[11px] uppercase tracking-widest font-bold flex items-center justify-center gap-1.5">
            <Pencil className="w-3.5 h-3.5" /> Editar
          </button>
        )}
        {art.status !== "removed" && (
          <button onClick={() => setConfirmDelete(true)} disabled={busy === "delete"} data-testid="spc-delete-btn"
            className="min-h-[44px] border border-red-300 bg-red-50 text-red-700 text-[11px] uppercase tracking-widest font-bold flex items-center justify-center gap-1.5 disabled:opacity-50">
            <Trash2 className="w-3.5 h-3.5" /> Eliminar del portal
          </button>
        )}
        <button onClick={() => setShowDistribution((v) => !v)} data-testid="spc-distribution-btn"
          className="h-11 border border-softblue bg-white text-blueblack text-[11px] uppercase tracking-widest font-bold">
          Cambiar distribución
        </button>
        {art.status !== "removed" && (fb?.status !== "published" || ig?.status !== "published") && (
          <button onClick={() => setPublishSocialOpen(true)} data-testid="spc-publish-social-btn"
            className="h-11 border border-emerald-600 bg-emerald-600 text-white text-[11px] uppercase tracking-widest font-bold flex items-center justify-center gap-1.5">
            Publicar en redes
          </button>
        )}
        {fb && fb.status !== "published" && fb.status !== "test_mode" && art.status !== "removed" && (
          <button onClick={() => doRetry("facebook")} disabled={busy === "retry-facebook"} data-testid="spc-retry-fb"
            className="h-11 border border-softblue bg-white text-blueblack text-[11px] uppercase tracking-widest font-bold flex items-center justify-center gap-1.5 disabled:opacity-50">
            {busy === "retry-facebook" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Reintentar FB
          </button>
        )}
        {ig && ig.status !== "published" && ig.status !== "test_mode" && art.status !== "removed" && (
          <button onClick={() => doRetry("instagram")} disabled={busy === "retry-instagram"} data-testid="spc-retry-ig"
            className="h-11 border border-softblue bg-white text-blueblack text-[11px] uppercase tracking-widest font-bold flex items-center justify-center gap-1.5 disabled:opacity-50">
            {busy === "retry-instagram" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Reintentar IG
          </button>
        )}
      </div>

      {showDistribution && (
        <div className="border border-softblue bg-white p-3 space-y-3" data-testid="spc-distribution-panel">
          <DistSelect label="Facebook" value={art.publish_to_facebook || "auto"}
            onChange={(v) => patchDist({ facebook: v })} testId="dist-fb" />
          <DistSelect label="Instagram" value={art.publish_to_instagram || "auto"}
            onChange={(v) => patchDist({ instagram: v })} testId="dist-ig" />
          <div className="text-[10px] text-textgray">Auto: motor decide. Sí: fuerza elegibilidad. No: bloquea.</div>
        </div>
      )}

      {/* Publicar en Redes modal (Turno 4) */}
      {publishSocialOpen && (
        <div data-testid="spc-publish-social-modal" className="fixed inset-0 z-50 bg-blueblack/70 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white w-full max-w-md p-4 space-y-3">
            <div className="font-serif text-lg font-bold text-blueblack">Publicar en redes</div>
            <div className="text-xs text-textgray">Se usará la imagen actual del artículo. Si publicaste con &quot;Solo Portal&quot; y cambiaste la imagen, se regenerará la placa automáticamente.</div>
            <div className="space-y-2 py-1">
              {fb?.status !== "published" && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="spc-social-cb-fb"
                    checked={!!socialSel.facebook}
                    onChange={(e) => setSocialSel((s) => ({ ...s, facebook: e.target.checked }))} />
                  <Facebook className="w-4 h-4 text-blue-600" /> Facebook
                </label>
              )}
              {ig?.status !== "published" && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="spc-social-cb-ig"
                    checked={!!socialSel.instagram}
                    onChange={(e) => setSocialSel((s) => ({ ...s, instagram: e.target.checked }))} />
                  <Instagram className="w-4 h-4 text-pink-600" /> Instagram
                </label>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setPublishSocialOpen(false)}
                data-testid="spc-social-cancel"
                className="flex-1 h-10 border border-softblue bg-white text-blueblack text-xs uppercase tracking-widest font-bold">
                Cancelar
              </button>
              <button onClick={doPublishSocial} disabled={busy === "publish-social"}
                data-testid="spc-social-confirm"
                className="flex-1 h-10 bg-emerald-600 text-white text-xs uppercase tracking-widest font-bold disabled:opacity-60">
                {busy === "publish-social" ? "Publicando…" : "Publicar ahora"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal §37 */}
      {confirmDelete && (
        <div data-testid="spc-confirm-delete" className="fixed inset-0 z-50 bg-blueblack/70 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white w-full max-w-md p-4 space-y-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <div className="font-serif text-lg font-bold text-blueblack">¿Eliminar esta noticia del portal?</div>
            </div>
            <p className="text-sm text-textgray">
              Se retirará de Sur Primicia. Las publicaciones ya realizadas en Facebook o Instagram <b>no serán eliminadas automáticamente</b>. Las publicaciones sociales pendientes serán canceladas.
            </p>
            <input value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Motivo (opcional)"
              className="w-full h-10 px-2 border border-softblue text-sm" />
            <div className="flex gap-2 pt-2">
              <button onClick={() => setConfirmDelete(false)} className="flex-1 h-11 border border-softblue text-blueblack text-[11px] uppercase tracking-widest font-bold">Cancelar</button>
              <button onClick={doDelete} disabled={busy === "delete"} data-testid="spc-delete-confirm"
                className="flex-1 h-11 bg-red-600 text-white text-[11px] uppercase tracking-widest font-bold disabled:opacity-50">
                {busy === "delete" ? <Loader2 className="w-3.5 h-3.5 mx-auto animate-spin" /> : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChannelRow({ icon: Icon, label, status, at, reason, permalink, nextRetry }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.unknown;
  let visual = "—";
  if (status === "published" || status === "test_mode") visual = "✓ Publicada";
  else if (["failed", "disconnected", "cancelled", "expired"].includes(status)) visual = "⚠ Error";
  else if (["queued", "publishing", "retrying"].includes(status)) visual = "⏱ En cola";
  else if (status === "paused") visual = "⚪ Pausada";
  else if (status === "not_selected") visual = "— No seleccionada";
  return (
    <div className="p-3 flex items-start gap-3">
      <Icon className={`w-5 h-5 ${s.text} mt-0.5`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="font-bold text-[11px] uppercase tracking-widest text-blueblack">{label}</div>
          <div className={`text-[11px] font-bold ${s.text}`}>{visual}</div>
        </div>
        <div className="text-[11px] text-textgray mt-0.5">
          {at && <>Última: {new Date(at).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" })}</>}
          {reason && <> · {reasonMsg(reason)}</>}
          {nextRetry && <> · Reintento {new Date(nextRetry).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}</>}
        </div>
        {permalink && <a href={permalink} target="_blank" rel="noreferrer" className="text-[11px] underline text-nightblue">Ver publicación</a>}
      </div>
    </div>
  );
}

function reasonMsg(code) {
  const map = {
    rate_limit: "Meta está limitando temporalmente las publicaciones.",
    token_expired: "Reconexión requerida.",
    missing_visual: "Sin imagen apta.",
    daily_limit: "Límite diario alcanzado.",
    admin_opt_out: "Bloqueada por el administrador.",
    saturation: "Muchas publicaciones del mismo evento.",
    channel_paused: "Canal pausado.",
    article_removed: "Noticia retirada.",
    score_below_threshold: "Prioridad insuficiente.",
  };
  return map[code] || code;
}

function DistSelect({ label, value, onChange, testId }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest font-bold text-blueblack mb-1">{label}</div>
      <div className="grid grid-cols-3 border border-softblue" data-testid={testId}>
        {["auto", "true", "false"].map((v) => (
          <button key={v} onClick={() => onChange(v)}
            className={`h-9 text-[11px] uppercase tracking-widest font-bold border-l border-softblue first:border-l-0 ${
              value === v ? "bg-blueblack text-white" : "bg-white text-blueblack"
            }`}>
            {v === "auto" ? "Auto" : v === "true" ? "Sí" : "No"}
          </button>
        ))}
      </div>
    </div>
  );
}
